import { useEffect, useRef, useState, useCallback } from "react";
import { supabase } from "../lib/supabase";
import { getLatestClusters, getLatestInsights } from "../lib/api";
import type { Cluster, AIInsight } from "../lib/types";

export function useClusters(questionId: string | null) {
  const [clusters, setClusters] = useState<Cluster[]>([]);
  const [insights, setInsights] = useState<AIInsight[]>([]);
  // Per-instance token so multiple useClusters mounted for the same
  // questionId never collide on Supabase's channel-by-name cache and so
  // a fast unmount/remount can't hand this instance an already-subscribed
  // channel (which throws on further .on() calls).
  const instanceRef = useRef<string>(crypto.randomUUID());

  const load = useCallback(async () => {
    if (!questionId) { setClusters([]); setInsights([]); return; }
    const [c, i] = await Promise.all([getLatestClusters(questionId), getLatestInsights(questionId)]);
    setClusters(c);
    setInsights(i);
  }, [questionId]);

  useEffect(() => {
    load();
    if (!questionId) return;

    const channel = supabase
      .channel(`clusters-${questionId}-${instanceRef.current}`)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "clusters", filter: `question_id=eq.${questionId}` }, () => load())
      .on("postgres_changes", { event: "DELETE", schema: "public", table: "clusters", filter: `question_id=eq.${questionId}` }, () => load())
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "ai_insights", filter: `question_id=eq.${questionId}` }, () => load())
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "ai_insights", filter: `question_id=eq.${questionId}` }, () => load())
      .on("postgres_changes", { event: "DELETE", schema: "public", table: "ai_insights", filter: `question_id=eq.${questionId}` }, () => load())
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [questionId, load]);

  return { clusters, insights, reload: load };
}
